import type { Policy } from "@agency/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { callAction } from "../api";
import { useConfig } from "./ConfigProvider";
import { useToast } from "./Toast";
import { Button, Input, Select, Sheet } from "./ui";
import { useT } from "../i18n";

const STATUSES = ["active", "bound", "quoted", "pending_cancel", "cancelled", "expired", "renewed"] as const;

interface Form {
  line: string; carrier: string; policyNumber: string; status: (typeof STATUSES)[number];
  effectiveDate: string; expirationDate: string; premium: string; priorPremium: string; billing: "direct" | "agency";
}

const today = () => new Date().toISOString().slice(0, 10);
const plusMonths = (iso: string, months: number) => { const d = new Date(iso); d.setUTCMonth(d.getUTCMonth() + months); return d.toISOString().slice(0, 10); };
const dollars = (cents: number | null | undefined) => (cents == null ? "" : (cents / 100).toFixed(2));
const cents = (s: string) => { const n = Number(s.replace(/[$,\s]/g, "")); return s.trim() === "" || isNaN(n) ? null : Math.round(n * 100); };

/**
 * Add or edit a policy by hand. Used from the customer page (Policies tab) and from the Policies screen.
 * `policy` set → edit mode (agency.policy.update); otherwise create (agency.policy.create) for `customerId`.
 */
export function PolicySheet({ open, onClose, customerId, policy, onSaved }: { open: boolean; onClose: () => void; customerId: string; policy?: Policy | null; onSaved?: (p: Policy) => void }) {
  const t = useT();
  const { config } = useConfig();
  const qc = useQueryClient();
  const { toast } = useToast();
  const blank = (): Form => ({ line: config.agency.lines[0] ?? "auto", carrier: "", policyNumber: "", status: "active", effectiveDate: today(), expirationDate: plusMonths(today(), 6), premium: "", priorPremium: "", billing: "direct" });
  const [form, setForm] = useState<Form>(blank);
  useEffect(() => {
    if (!open) return;
    setForm(policy ? { line: policy.line, carrier: policy.carrier, policyNumber: policy.policyNumber, status: policy.status, effectiveDate: policy.effectiveDate.slice(0, 10), expirationDate: policy.expirationDate.slice(0, 10), premium: dollars(policy.premiumCents), priorPremium: dollars(policy.priorTermPremiumCents), billing: policy.billing } : blank());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, policy?.id]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["customer", customerId] });
    qc.invalidateQueries({ queryKey: ["policies"] });
    qc.invalidateQueries({ queryKey: ["renewals"] });
    qc.invalidateQueries({ queryKey: ["today"] });
    qc.invalidateQueries({ queryKey: ["pipeline"] });
  };
  const m = useMutation({
    mutationFn: () => {
      const premiumCents = cents(form.premium) ?? 0;
      const priorTermPremiumCents = cents(form.priorPremium);
      const dates = { effectiveDate: new Date(`${form.effectiveDate}T00:00:00Z`).toISOString(), expirationDate: new Date(`${form.expirationDate}T00:00:00Z`).toISOString() };
      if (policy) {
        return callAction<"agency.policy.update", Policy>("agency.policy.update", { policyId: policy.id, patch: { line: form.line as Policy["line"], carrier: form.carrier, policyNumber: form.policyNumber, status: form.status, ...dates, premiumCents, priorTermPremiumCents, billing: form.billing } });
      }
      return callAction<"agency.policy.create", Policy>("agency.policy.create", { customerId, line: form.line as Policy["line"], carrier: form.carrier, policyNumber: form.policyNumber, status: form.status, ...dates, premiumCents, priorTermPremiumCents, billing: form.billing, coverageSnapshot: {}, externalIds: {} });
    },
    onSuccess: (r) => {
      invalidate();
      toast({ title: policy ? t("policies.saved") : t("policies.added"), body: `${r.result.carrier} ${r.result.policyNumber}`, tone: "success" });
      onSaved?.(r.result);
      onClose();
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const submit = (e: FormEvent) => { e.preventDefault(); if (form.carrier.trim() && form.policyNumber.trim()) m.mutate(); };

  return (
    <Sheet open={open} onClose={onClose} title={policy ? t("policies.edit") : t("policies.add")}>
      <form onSubmit={submit} className="space-y-3">
        <Select label={t("common.line")} value={form.line} onChange={(e) => set("line", e.target.value)}>
          {config.agency.lines.map((l) => <option key={l} value={l}>{l.replace(/_/g, " ")}</option>)}
        </Select>
        <div>
          <Input label={t("common.carrier")} required list="policy-carriers" value={form.carrier} onChange={(e) => set("carrier", e.target.value)} />
          <datalist id="policy-carriers">{config.agency.carriers.map((c) => <option key={c.name} value={c.name} />)}</datalist>
          <p className="mt-1 text-xs text-ink/50">{t("policies.carrierHint")}</p>
        </div>
        <Input label={t("policies.number")} required value={form.policyNumber} onChange={(e) => set("policyNumber", e.target.value)} />
        <Select label={t("common.status")} value={form.status} onChange={(e) => set("status", e.target.value as Form["status"])}>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`policy.status.${s}`)}</option>)}
        </Select>
        <div className="grid grid-cols-2 gap-3">
          <Input type="date" label={t("policies.effective")} required value={form.effectiveDate} onChange={(e) => set("effectiveDate", e.target.value)} />
          <Input type="date" label={t("policies.expiration")} required value={form.expirationDate} onChange={(e) => set("expirationDate", e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label={t("common.premium")} inputMode="decimal" placeholder="842.50" value={form.premium} onChange={(e) => set("premium", e.target.value)} />
          <Input label={t("policies.priorPremium")} inputMode="decimal" value={form.priorPremium} onChange={(e) => set("priorPremium", e.target.value)} />
        </div>
        <Select label={t("policies.billing")} value={form.billing} onChange={(e) => set("billing", e.target.value as Form["billing"])}>
          <option value="direct">{t("policies.billing.direct")}</option>
          <option value="agency">{t("policies.billing.agency")}</option>
        </Select>
        <p className="text-xs text-ink/55">{t("policies.manualNote")}</p>
        <Button type="submit" size="lg" className="w-full" loading={m.isPending}>{t("common.save")}</Button>
      </form>
    </Sheet>
  );
}
